import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';
import type { Usuario } from '../types/auth';

interface ReporteProblemaParams {
  readonly mensaje: string;
  readonly screenshot?: Uint8Array | null;
  readonly usuario: Usuario;
  /** ID del evento de Sentry ya capturado (ej: por un toast de error) al que este reporte da contexto humano. */
  readonly sentryEventId?: string;
}

async function obtenerNombreProvincia(provinciaId: string | null): Promise<string> {
  if (!provinciaId) return 'N/A';
  const { data } = await supabase
    .from('provincias_eclesiasticas')
    .select('nombre')
    .eq('id', provinciaId)
    .maybeSingle();
  return data?.nombre ?? 'N/A';
}

/**
 * Envía un reporte de problema del usuario a Sentry vía la API headless de feedback,
 * etiquetado con la ubicación completa (lumisial, diócesis, provincia, ciudad, región)
 * y el contexto (URL, ruta). La captura de pantalla, si existe, viaja como adjunto.
 */
export async function enviarReporteProblema({ mensaje, screenshot, usuario, sentryEventId }: ReporteProblemaParams): Promise<void> {
  const provincia = await obtenerNombreProvincia(usuario.provincia_eclesiastica_id);

  const tags: Record<string, string> = {
    origen: sentryEventId ? 'toast-error' : 'reporte-usuario',
    lumisial: usuario.lumisial?.nombre ?? 'N/A',
    diocesis: usuario.diocesis?.nombre ?? 'N/A',
    provincia_eclesiastica: provincia,
    ciudad: usuario.lumisial?.ciudad ?? 'N/A',
    region: usuario.lumisial?.region ?? 'N/A',
    usuario_email: usuario.email,
  };

  const attachments = screenshot
    ? [{ filename: 'captura.png', data: screenshot }]
    : undefined;

  Sentry.captureFeedback(
    {
      message: mensaje,
      name: `${usuario.nombres} ${usuario.apellidos}`.trim(),
      email: usuario.email,
      // Engancha el reporte con el error en la propia interfaz de Sentry: desde el feedback
      // se llega al error y viceversa. Sin esto quedan sueltos y hay que cruzarlos a mano.
      ...(sentryEventId ? { associatedEventId: sentryEventId } : {}),
    },
    {
      captureContext: {
        tags,
        extra: {
          url: globalThis.location.href,
          ruta: globalThis.location.pathname,
        },
      },
      attachments,
    },
  );
}
